import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Collapse, useTheme, Stack } from '@mui/material';
import {
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport
} from '@mui/x-data-grid-premium';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';

export const CustomToolbar = ({ isPinned, setIsPinned, pinNeeded = false, modalOpen, setModalOpen, modalNeeded = false }) => {
    const [expanded, setExpanded] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isCompact, setIsCompact] = useState(false);
    const theme = useTheme();
    const toolbarRef = useRef(null);

    useEffect(() => {
        const checkWidth = () => {
            const gridElement = toolbarRef.current?.closest('.MuiDataGrid-root')?.parentElement;
            if (gridElement) {
                const width = gridElement.getBoundingClientRect().width;
                setIsCompact(width < 400);
            }
        };
        
        // Check initially
        checkWidth();
    }, [isFullScreen]); // Re-check when fullscreen mode changes

    const handleToggle = () => {
        setExpanded((prev) => !prev);
    };

    const handleFullScreenToggle = (event) => {
        const toolbarElement = event.currentTarget.closest('.MuiDataGrid-toolbarContainer');
        const gridElement = toolbarElement?.closest('.MuiDataGrid-root')?.parentElement;

        if (gridElement) {
            setIsFullScreen(prev => !prev);

            if (!isFullScreen) {
                const originalStyles = {
                    position: gridElement.style.position,
                    top: gridElement.style.top,
                    left: gridElement.style.left,
                    width: gridElement.style.width,
                    height: gridElement.style.height,
                    zIndex: gridElement.style.zIndex,
                    background: gridElement.style.background,
                    padding: gridElement.style.padding,
                    overflow: gridElement.style.overflow,
                    flex: gridElement.style.flex,
                    maxWidth: gridElement.style.maxWidth,
                    minWidth: gridElement.style.minWidth
                };

                gridElement.dataset.originalStyles = JSON.stringify(originalStyles);

                // Apply fullscreen styles
                Object.assign(gridElement.style, {
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    width: '100vw',
                    height: '100vh',
                    zIndex: '1300',
                    background: theme.palette.background.paper,
                    padding: '20px',
                    boxSizing: 'border-box',
                    overflow: 'auto',
                    flex: 'none',
                    maxWidth: 'none',
                    minWidth: 'none'
                });

                // Adjust the DataGrid root element
                const dataGrid = gridElement.querySelector('.MuiDataGrid-root');
                if (dataGrid) {
                    dataGrid.style.width = 'calc(100vw - 40px)';
                    dataGrid.style.height = 'calc(100vh - 40px)';
                    dataGrid.style.maxWidth = 'none';
                }

                // Store original parent overflow
                const parentStack = gridElement.closest('.MuiStack-root');
                if (parentStack) {
                    gridElement.dataset.originalParentOverflow = parentStack.style.overflow;
                    parentStack.style.overflow = 'visible';
                }
            } else {
                // Restore original styles
                const originalStyles = JSON.parse(gridElement.dataset.originalStyles || '{}');
                Object.assign(gridElement.style, originalStyles);

                // Reset DataGrid dimensions
                const dataGrid = gridElement.querySelector('.MuiDataGrid-root');
                if (dataGrid) {
                    dataGrid.style.width = '';
                    dataGrid.style.height = '';
                    dataGrid.style.maxWidth = '';
                }

                // Restore parent overflow
                const parentStack = gridElement.closest('.MuiStack-root');
                if (parentStack && gridElement.dataset.originalParentOverflow !== undefined) {
                    parentStack.style.overflow = gridElement.dataset.originalParentOverflow;
                }

                delete gridElement.dataset.originalStyles;
                delete gridElement.dataset.originalParentOverflow;
            }
        }
    };

    const renderToolbarContent = () => {
        if (isCompact) {
            return (
                <>
                    <Tooltip title="Columns">
                        <IconButton size="small">
                            <ViewWeekOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Filters">
                        <IconButton size="small">
                            <TuneRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Density">
                        <IconButton size="small">
                            <ViewComfyIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export">
                        <IconButton size="small">
                            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </>
            );
        }

        return (
            <>
                <GridToolbarColumnsButton
                    slotProps={{
                        button: {
                            startIcon: <ViewWeekOutlinedIcon />,
                            sx: { fontWeight: 500 }
                        }
                    }}
                />
                <GridToolbarFilterButton
                    sx={{ fontWeight: 500 }}
                    slotProps={{
                        button: {
                            startIcon: <TuneRoundedIcon />,
                            sx: { fontWeight: 500 }
                        }
                    }}
                />
                <GridToolbarDensitySelector />
                <GridToolbarExport />
            </>
        );
    };

    return (
        <GridToolbarContainer
            ref={toolbarRef}
            sx={{
                background: theme.palette.customColors.mediumBackground,
                borderBottom: '1px solid #e0e0e0',
                justifyContent: 'space-between',
                '& .MuiButton-root': {
                    background: 'transparent',
                    color: '#0048E5 !important',
                },
                alignItems: 'center'
            }}
        >
            <Stack direction="row" sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Tooltip title={expanded ? "Close menu" : "Open menu"}>
                        <IconButton onClick={handleToggle} size="large" sx={{ px: 1, py: 0 }}>
                            <MenuOpenIcon sx={{ fontSize: 21, p: 0 }} />
                        </IconButton>
                    </Tooltip>
                    <Collapse in={expanded} orientation="horizontal">
                        <Box sx={{ display: 'flex', gap: 1, fontWeight: 500 }}>
                            {renderToolbarContent()}
                            {pinNeeded && (
                                <Tooltip title={isPinned ? "Unpin Rows" : "Pin Rows"}>
                                    <IconButton onClick={() => setIsPinned(!isPinned)} size="small">
                                        {isPinned ? <PushPinIcon sx={{ fontSize: 18, p: 0 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 18, p: 0 }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {modalNeeded && (
                                <Tooltip title="Open Table Modal">
                                    <IconButton onClick={() => setModalOpen(!modalOpen)} size="small">
                                        <FindInPageOutlinedIcon sx={{ fontSize: 18, p: 0 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Collapse>
                </Box>

                <Tooltip title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                    <IconButton onClick={handleFullScreenToggle} size="small" sx={{ alignSelf: 'flex-end' }}>
                        {isFullScreen ? 
                            <FullscreenExitIcon sx={{ fontSize: 21, p: 0 }} /> : 
                            <FullscreenIcon sx={{ fontSize: 21, p: 0 }} />
                        }
                    </IconButton>
                </Tooltip>
            </Stack>
        </GridToolbarContainer>
    );
};

export default CustomToolbar;